import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Store scroll positions globally
const scrollPositions = new Map();

export const useScrollMemory = () => {
  const location = useLocation();

  // Save scroll position when component unmounts or location changes
  useEffect(() => {
    const saveScrollPosition = () => {
      // Always use window scroll
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
      const position = { scrollTop, scrollLeft, timestamp: Date.now() };
      scrollPositions.set(location.pathname, position);
      // Also save to sessionStorage for persistence across page reloads
      sessionStorage.setItem(`scroll_${location.pathname}`, JSON.stringify(position));
      console.log(`💾 Saved window scroll position for ${location.pathname}:`, position);
    };

    // Save scroll position before leaving
    return () => {
      saveScrollPosition();
    };
  }, [location.pathname]);

  // Scroll to top when component mounts (disable scroll memory restoration)
  useEffect(() => {
    const scrollToTop = () => {
      // Always scroll to top when navigating to a new page
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
      console.log(`📍 Scrolling to top for ${location.pathname}`);
    };

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(scrollToTop, 100);
    
    return () => clearTimeout(timeoutId);
  }, [location.pathname]);

  // Function to manually save current scroll position
  const saveCurrentPosition = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    scrollPositions.set(location.pathname, { scrollTop, scrollLeft });
    console.log(`💾 Manually saved window scroll position for ${location.pathname}:`, { scrollTop, scrollLeft });
  };

  // Function to clear saved position for current page
  const clearSavedPosition = () => {
    scrollPositions.delete(location.pathname);
    console.log(`🗑️ Cleared saved scroll position for ${location.pathname}`);
  };

  // Function to clear all saved positions
  const clearAllPositions = () => {
    scrollPositions.clear();
    console.log(`🗑️ Cleared all saved scroll positions`);
  };

  return {
    saveCurrentPosition,
    clearSavedPosition,
    clearAllPositions,
    hasSavedPosition: scrollPositions.has(location.pathname)
  };
};

export default useScrollMemory;
